import { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useTenantStore from '../stores/TenantStore';
import OpenAPI from '../endpoint/Openapi';
import type { components } from '../endpoint/Schema';
import { applyTenantColors } from '../utils/colorUtils';

type TenantInfoDto = components['schemas']['TenantInfoDto'];

const SELECTED_TENANT_KEY = 'selected_tenant';
const TENANTS_CACHE_KEY = 'user_tenants_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface TenantCache {
  tenants: TenantInfoDto[];
  timestamp: number;
}

export const useTenantQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setTenantConfig, id: currentTenantId } = useTenantStore();
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  const tenantsCache = useRef<TenantInfoDto[] | null>(null);
  const lastUrlTenantId = useRef<string | null>(null);

  // Get tenantId from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const urlTenantId = urlParams.get('tenantId');

  // Load tenants from cache or API
  const loadTenants = useCallback(async (): Promise<TenantInfoDto[]> => {
    // Check if we have cached tenants
    if (tenantsCache.current) {
      return tenantsCache.current;
    }

    // Check localStorage cache
    const cachedData = localStorage.getItem(TENANTS_CACHE_KEY);
    if (cachedData) {
      try {
        const cache: TenantCache = JSON.parse(cachedData);
        const now = Date.now();
        if (now - cache.timestamp < CACHE_DURATION) {
          tenantsCache.current = cache.tenants;
          return cache.tenants;
        }
      } catch (error) {
        // Invalid cache, continue to fetch from API
      }
    }

    // Fetch from API
    try {
      const response = await OpenAPI.GET(
        '/api/v2/admin/configuration/user-tenants',
      );

      if (response.data) {
        // Cache the results
        tenantsCache.current = response.data;
        const cache: TenantCache = {
          tenants: response.data,
          timestamp: Date.now(),
        };
        localStorage.setItem(TENANTS_CACHE_KEY, JSON.stringify(cache));
        return response.data;
      }
    } catch (error) {
      // Handle API error
    }

    return [];
  }, []);

  // Add tenantId to URL if missing
  const addTenantIdToUrl = useCallback(
    (tenantId: string) => {
      const newParams = new URLSearchParams(location.search);
      newParams.set('tenantId', tenantId);

      const newUrl = `${location.pathname}?${newParams.toString()}`;
      navigate(newUrl, { replace: true });
    },
    [location.search, location.pathname, navigate],
  );

  // Remove tenantId from URL
  const removeTenantIdFromUrl = useCallback(() => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('tenantId');

    const newUrl = newParams.toString()
      ? `${location.pathname}?${newParams.toString()}`
      : location.pathname;
    navigate(newUrl, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Switch to a different tenant
  const switchToTenant = useCallback(
    async (tenantId: string) => {
      setIsLoadingTenant(true);
      setTenantError(null);

      try {
        const tenants = await loadTenants();
        const tenant = tenants.find((t: TenantInfoDto) => t.id === tenantId);

        if (tenant) {
          // Update tenant store immediately for faster UI updates
          setTenantConfig(tenant);

          // Update localStorage
          localStorage.setItem(SELECTED_TENANT_KEY, JSON.stringify(tenant));

          // Apply tenant colors
          if (tenant.appColors) {
            applyTenantColors(tenant.appColors);
          }

          setIsLoadingTenant(false);
          return true;
        } else {
          // Tenant not found in user's available tenants
          setTenantError(
            `You don't have permission to access tenant: ${tenantId}`,
          );
          setIsLoadingTenant(false);
          return false;
        }
      } catch (error) {
        setTenantError('Failed to load tenant information');
        setIsLoadingTenant(false);
        return false;
      }
    },
    [setTenantConfig, loadTenants],
  );

  // Clear tenant error
  const clearTenantError = useCallback(() => {
    setTenantError(null);
  }, []);

  // Main effect to handle tenant sync
  useEffect(() => {
    const syncTenantWithUrl = async () => {
      // Avoid unnecessary processing if URL tenant ID hasn't changed
      if (lastUrlTenantId.current === urlTenantId) {
        return;
      }
      lastUrlTenantId.current = urlTenantId;

      // Clear any previous errors
      setTenantError(null);

      // If there's a tenantId in URL but no current tenant, or they don't match
      if (urlTenantId && urlTenantId !== currentTenantId) {
        const success = await switchToTenant(urlTenantId);
        if (!success) {
          // Keep the invalid tenantId in URL to show error, don't remove it
          // This allows user to see the error message
        }
      }
      // If there's a current tenant but no tenantId in URL, add it
      else if (currentTenantId && !urlTenantId) {
        addTenantIdToUrl(currentTenantId);
      }
    };

    // Only run if we're authenticated and have access to the tenant store
    if (currentTenantId !== undefined) {
      void syncTenantWithUrl();
    }
  }, [urlTenantId, currentTenantId, switchToTenant, addTenantIdToUrl]);

  return {
    urlTenantId,
    tenantError,
    isLoadingTenant,
    addTenantIdToUrl,
    removeTenantIdFromUrl,
    switchToTenant,
    clearTenantError,
  };
};
